import { useEffect, useRef } from 'react'
import styled from 'styled-components'

const InfiniteScrollSentinel = ({
  hasMore,
  loading,
  onLoadMore,
  rootMargin = '300px',
  root = null,
  label = 'Loading more…',
}) => {
  const ref = useRef(null)
  const onLoadMoreRef = useRef(onLoadMore)
  onLoadMoreRef.current = onLoadMore

  useEffect(() => {
    const node = ref.current
    if (!node || !hasMore || loading) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onLoadMoreRef.current?.()
      },
      { root: root || null, rootMargin }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, loading, rootMargin, root])

  if (!hasMore && !loading) return null

  return (
    <Sentinel ref={ref} aria-hidden="true">
      {loading && (
        <Loader>
          <Spinner />
          <span>{label}</span>
        </Loader>
      )}
    </Sentinel>
  )
}

export default InfiniteScrollSentinel

const Sentinel = styled.div`
  width: 100%;
  min-height: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ children }) => (children ? '18px 0' : '1px 0')};
`

const Loader = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors?.grey?.[800] || '#6e6e78'};
`

const Spinner = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors?.lightGrey || '#e4e4e7'};
  border-top-color: ${({ theme }) => theme.colors?.secondary || '#33becc'};
  animation: infiniteScrollSpin 0.7s linear infinite;

  @keyframes infiniteScrollSpin {
    to { transform: rotate(360deg); }
  }
`
